import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, UserPlus, Users, Shield, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/utils/axiosConfig';
import CreateUserForm from './CreateUserForm';
import CompanyPermissionsEditor from './CompanyPermissionsEditor';
import ToolsPermissionsEditor from './ToolsPermissionsEditor';
import UserRow from './UserRow';

const AdminPanel = ({ user, onBack, onLogout }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [editingToolsPermissions, setEditingToolsPermissions] = useState(null);
  const [availableApps, setAvailableApps] = useState([]);
  const [availableToolsApps, setAvailableToolsApps] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedToolsPermissions, setSelectedToolsPermissions] = useState([]);
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    password: '',
    full_name: ''
  });
  const [editUserForm, setEditUserForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchAvailableApps();
    fetchAvailableToolsApps();
    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      await axiosInstance.get('/verify-token');
    } catch (error) {
      console.error('Token verification failed:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/admin/users');
      setUsers(response.data.users);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Eroare la încărcarea utilizatorilor');
      }
    }
  };

  const fetchAvailableApps = async () => {
    try {
      const response = await axiosInstance.get('/available-apps');
      setAvailableApps(response.data.apps);
    } catch (error) {
      console.error('Error fetching available apps:', error);
    }
  };

  const fetchAvailableToolsApps = async () => {
    try {
      const response = await axiosInstance.get('/available-tools-apps');
      setAvailableToolsApps(response.data.apps);
    } catch (error) {
      console.error('Error fetching available tools apps:', error);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // --- Create User ---
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/admin/create-user', createUserForm);
      toast.success('Utilizator creat cu succes!');
      setCreateUserForm({ username: '', email: '', password: '', full_name: '' });
      setShowCreateUser(false);
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la crearea utilizatorului');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Edit User ---
  const handleEditUser = (u) => {
    setEditingUser(u._id);
    setEditUserForm({
      full_name: u.full_name,
      email: u.email,
      password: '',
      role: u.role,
      is_active: u.is_active ?? true
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditUserForm({ full_name: '', email: '', password: '', role: '', is_active: true });
  };

  const handleUpdateUser = async (userId) => {
    setLoading(true);
    try {
      const updateData = {
        full_name: editUserForm.full_name,
        email: editUserForm.email,
        role: editUserForm.role,
        is_active: editUserForm.is_active
      };
      if (editUserForm.password) {
        updateData.password = editUserForm.password;
      }
      await axiosInstance.put(`/admin/update-user/${userId}`, updateData);
      toast.success('Utilizator actualizat cu succes!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la actualizarea utilizatorului');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Sigur dorești să ștergi utilizatorul "${username}"?`)) return;
    try {
      await axiosInstance.delete(`/admin/delete-user/${userId}`);
      toast.success('Utilizator șters cu succes!');
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la ștergerea utilizatorului');
      }
    }
  };

  // --- Company Permissions ---
  const handleEditPermissions = (u) => {
    setEditingPermissions(u._id);
    setSelectedPermissions(u.app_permissions || []);
  };

  const handleTogglePermission = (appId) => {
    setSelectedPermissions(prev =>
      prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]
    );
  };

  const handleSavePermissions = async (userId) => {
    setLoading(true);
    try {
      await axiosInstance.put(`/admin/update-user-permissions/${userId}`, {
        app_ids: selectedPermissions
      });
      toast.success('Permisiuni Company Area actualizate cu succes!');
      setEditingPermissions(null);
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la actualizarea permisiunilor');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Tools Permissions ---
  const handleEditToolsPermissions = (u) => {
    setEditingToolsPermissions(u._id);
    setSelectedToolsPermissions(u.tools_permissions || []);
  };

  const handleToggleToolsPermission = (appId) => {
    setSelectedToolsPermissions(prev =>
      prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]
    );
  };

  const handleSaveToolsPermissions = async (userId) => {
    setLoading(true);
    try {
      await axiosInstance.put(`/admin/update-user-tools-permissions/${userId}`, {
        app_ids: selectedToolsPermissions
      });
      toast.success('Permisiuni Tools Area actualizate cu succes!');
      setEditingToolsPermissions(null);
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la actualizarea permisiunilor');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Deploy Access ---
  const handleToggleDeployAccess = async (userId, currentAccess) => {
    setLoading(true);
    try {
      await axiosInstance.put(`/admin/update-user-deploy-access/${userId}`, {
        has_access: !currentAccess
      });
      toast.success(`Acces Deploy ${!currentAccess ? 'acordat' : 'revocat'} cu succes!`);
      fetchUsers();
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Eroare la actualizarea accesului Deploy');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-elegant-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Panou Administrator</h1>
              <p className="text-xs text-muted-foreground">Gestionare utilizatori și setări</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-card border-border rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent-light/50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Utilizatori</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="total-users-count">{users.length}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Administratori</p>
                  <p className="text-2xl font-bold text-foreground">
                    {users.filter(u => u.role === 'supreme_admin' || u.role === 'admin').length}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-2xl p-6">
              <Button
                className="w-full h-full rounded-xl"
                variant="premium"
                onClick={() => setShowCreateUser(true)}
                data-testid="add-user-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Adaugă Utilizator
              </Button>
            </Card>
          </div>

          {/* Create User Form */}
          {showCreateUser && (
            <CreateUserForm
              form={createUserForm}
              setForm={setCreateUserForm}
              onSubmit={handleCreateUser}
              onCancel={() => setShowCreateUser(false)}
              loading={loading}
            />
          )}

          {/* Users List */}
          <Card className="bg-card border-border rounded-2xl p-8">
            <h3 className="text-xl font-bold text-foreground mb-6">Utilizatori Înregistrați</h3>
            
            {/* Company Permissions Editor */}
            {editingPermissions && (
              <CompanyPermissionsEditor
                userId={editingPermissions}
                userName={users.find(u => u._id === editingPermissions)?.full_name}
                availableApps={availableApps}
                selectedPermissions={selectedPermissions}
                onToggle={handleTogglePermission}
                onSave={handleSavePermissions}
                onCancel={() => setEditingPermissions(null)}
                loading={loading}
              />
            )}

            {/* Tools Permissions Editor */}
            {editingToolsPermissions && (
              <ToolsPermissionsEditor
                userId={editingToolsPermissions}
                userName={users.find(u => u._id === editingToolsPermissions)?.full_name}
                availableApps={availableToolsApps}
                selectedPermissions={selectedToolsPermissions}
                onToggle={handleToggleToolsPermission}
                onSave={handleSaveToolsPermissions}
                onCancel={() => setEditingToolsPermissions(null)}
                loading={loading}
              />
            )}
            
            <div className="space-y-4">
              {users.map((u) => (
                <UserRow
                  key={u._id}
                  u={u}
                  currentUser={user}
                  isEditing={editingUser === u._id}
                  editForm={editUserForm}
                  setEditForm={setEditUserForm}
                  onEdit={handleEditUser}
                  onCancelEdit={handleCancelEdit}
                  onUpdate={handleUpdateUser}
                  onDelete={handleDeleteUser}
                  onEditPermissions={handleEditPermissions}
                  onEditToolsPermissions={handleEditToolsPermissions}
                  onToggleDeployAccess={handleToggleDeployAccess}
                  loading={loading}
                />
              ))}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
